import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  useTranslation,
  Drawer,
  InputTypes,
  useMasterData,
  DropDown,
  AccountType,
  Search,
  MLPVersion,
} from '@nextgen-web-framework/all';
import { useSelector } from 'react-redux';
import { type AppState } from '@nextgen-web-framework/core';
import type { RegistrationValidations } from '@commerce-profile-webcomponents/sdk';
import { Field, FieldProps, useField } from 'formik';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import { DropDownOptionData, DropDownSelectType, DropDownwithMasterData } from './types/propTypes';
import styles from './DropDownwithMasterData.module.scss';

const DropDownwithMasterData = ({
  fieldName,
  value,
  onSelect,
  disabled,
  translationPrefix,
  showSearch,
  dropDownSelectType = DropDownSelectType.DRAWER,
  mlpVersionProp = MLPVersion.TWO,
}: DropDownwithMasterData) => {
  const t = useTranslation();
  const uniqueId = useId();
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // const [error, setError] = useState('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [results, setResults] = useState<string[]>();
  const inputRef = useRef(null);
  const isDrawerType = dropDownSelectType === DropDownSelectType.DRAWER;
  const config = useMasterData<RegistrationValidations[]>('RegistrationFieldsMasterData');
  const configs = ConfigService.getConfigs();
  const mlpVersionConfig = configs?.NEXT_INPUT_MLP_VERSION as MLPVersion;
  const mlpVersion = mlpVersionConfig ?? mlpVersionProp;
  const [, , fieldHandler] = useField(fieldName);

  const options = useMemo(() => {
    const fieldInfo = config?.find((c: RegistrationValidations) => c.pk === fieldName);
    const options = (fieldInfo?.options?.[selectedAccountType] as string[]) ?? (fieldInfo?.options?.ALL as string[]);
    return options;
  }, [fieldName, config, selectedAccountType]);

  useEffect(() => {
    if (isDrawerOpen) {
      onClearHandler();
    }
  }, [isDrawerOpen]);

  function getTranslatedData(key: string) {
    return t(translationPrefix ? `${translationPrefix}.${key}` : `profile.ProfileInfo.${fieldName}.${key}`);
  }

  const onClearHandler = useCallback(() => {
    setSearchKeyword('');
    setResults(options);
  }, [options]);

  const searchOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentValue = e.target.value;
      setSearchKeyword(currentValue);
      if (currentValue?.length) {
        const filteredResults = options.filter(
          (item: string) =>
            getTranslatedData(item)?.toLocaleLowerCase()?.indexOf(currentValue?.toLocaleLowerCase()) !== -1,
        );
        setResults(filteredResults);
      } else {
        setResults(options);
      }
    },
    [options],
  );

  const handleClick = (item: string) => {
    onSelect?.(item);
    setIsDrawerOpen(false);
    fieldHandler.setError('');
  };

  const handlerDrawer = () => (
    <Drawer
      header={t(`profile.ProfileInfo.${fieldName}`)}
      closeIcon
      onClose={() => {
        if (value?.length === 0) {
          fieldHandler.setError(`profile.validationMsg.${fieldName}.required`);
        }
        setIsDrawerOpen(false);
      }}
      subHeader={
        showSearch ? (
          <div className={styles['search-box']} data-testId="searchBox">
            <Search
              mlpVersion={MLPVersion.TWO}
              onChange={searchOnChange}
              value={searchKeyword}
              onSearch={() => null}
              onClear={onClearHandler}
              ref={inputRef}
            />
          </div>
        ) : null
      }
      bottomGap={0}
      useReactPortal={true}
      portalContainerId={'react-portal-drawer'}
    >
      <div className={styles['dropdown-drawer']} data-testid="dropdown-drawer">
        <div className={styles['dropdown-results']}>
          {results?.map((item, i) => (
            <div
              onClick={() => handleClick(item)}
              onKeyDown={() => handleClick(item)}
              tabIndex={0}
              data-testid={`${fieldName}${i}`}
              className={styles['dropdown']}
              key={uniqueId + i.toString}
              role="button"
              aria-label="dropdown-list"
            >
              {getTranslatedData(item)}
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );

  return (
    <div data-testid="field-input-container">
      {isDrawerOpen && handlerDrawer()}
      <Field type="text" name={fieldName}>
        {({ meta }: FieldProps) => (
          <div
            data-testid={`${fieldName}-input`}
            onClick={() => {
              if (isDrawerType) {
                setIsDrawerOpen(true);
              }
            }}
            tabIndex={-1}
            aria-hidden="true"
          >
            <DropDown
              id={fieldName}
              data-testid={fieldName}
              type={InputTypes.TEXT}
              name={fieldName}
              value={value ? getTranslatedData(value) : ''}
              dropdownLabel={t(`profile.ProfileInfo.${fieldName}`)}
              placeholder={t(`profile.ProfileInfo.${fieldName}`)}
              disabled={disabled}
              status={inputErrorStatus(meta.touched, meta.error)}
              message={inputErrorMessage(t, meta.touched, meta.error)}
              className={styles['dropdown']}
              onClick={() => {
                if (isDrawerType) {
                  setIsDrawerOpen(true);
                }
              }}
              mlpVersion={mlpVersion}
              configData={{
                label: 'name',
                disableItemKey: 'isDisabled',
                onSelect: (item: DropDownOptionData) => {
                  onSelect?.(item.id as string);
                },
              }}
              data={!isDrawerType ? options?.map((item) => ({ id: item, name: getTranslatedData(item) })) : []}
              disabledFilter
            />
          </div>
        )}
      </Field>
    </div>
  );
};
export default DropDownwithMasterData;
